import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Ruler,
  ArrowRight,
  Bookmark,
  Trash2,
  Link2 as LinkIcon,
} from "lucide-react";
import { PageHeader } from "./fordon";
import { FitPlan } from "@/components/FitPlan";
import { DeferredIsland } from "@/components/DeferredIsland";

const loadTurnSim = () => import("@/components/islands/TurnSimIsland");
const loadArSilhouette = () => import("@/components/islands/ArSilhouetteIsland");
import { loadSites, removeSite, saveSite, type SavedSite } from "@/lib/fit-sites";
import { getFyndanuListings, type FyndanuListing } from "@/lib/fyndanu.functions";
import {
  BODY_DEFAULTS,
  BODY_LABEL,
  DEFAULT_SITE,
  dimsFor,
  evaluateFit,
  mirrorWidth,
  verdict,
  type BodyType,
  type Site,
  type VehicleDims,
} from "@/lib/vehicle-fit";

const URL_SELF = "https://www.akhusbilfritid.se/verktyg/passar-den-hos-mig";

const FAQ = [
  {
    q: "Hur brett behöver jag ha för att parkera en husbil hemma?",
    a: "Räkna med karossbredden 2,25–2,35 m plus 60 cm utfällda backspeglar. För att kunna gå längs långsidan och öppna entrédörren behöver du 3,6–3,8 m fri bredd. Under 3,2 m blir det trångt och du måste fälla in speglarna varje gång.",
  },
  {
    q: "Hur hög är en husbil egentligen?",
    a: "Halvintegrerade ligger på 2,80–2,95 m, alkovmodeller 3,00–3,20 m och campervans 2,55–2,65 m. Takmonterad AC eller solpaneler lägger på 10–25 cm. Mät alltid mot fordonets faktiska höjd, inte broschyrmåttet.",
  },
  {
    q: "Får en husbil plats i ett vanligt garage?",
    a: "Sällan. Ett villagarage har oftast 2,10–2,30 m portöppning i höjd, vilket är minst 50 cm för lågt för en husbil. Carport eller kallförvaring med 3,5 m fri höjd är det som fungerar i praktiken.",
  },
  {
    q: "Hur mycket plats tar markisen?",
    a: "En standardmarkis fälls ut 2,0–2,7 m från karossen. Planera för att kunna fälla ut den mot den öppna sidan – annars sitter du med den mot grannens häck.",
  },
  {
    q: "Vad händer på vintern?",
    a: "I Norrbotten äter snövallarna 20–40 cm per sida från februari. Har du bara 20 cm marginal i oktober kommer fordonet inte in i mars. Räkna in snöröjning eller vinterförvaring i kalkylen.",
  },
  {
    q: "Hur mycket gata behövs för att svänga in med en husbil?",
    a: "En 7,4 m halvintegrerad har cirka 13 m vändcirkel. Med en 3,5 m bred infart i rät vinkel mot gatan krävs i praktiken 6,5–7,5 m fri gatubredd för att komma in i ett svep – frontöverhänget svänger ut över motsatt kant. Är gatan smalare backar du in eller breddar infartsöppningen till 4,5–5 m.",
  },
  {
    q: "Är det lättare att backa in än att köra in?",
    a: "Ja, ofta. Vid backning svänger bakvagnen in först och du kan använda hela gatans bredd i två drag. Med husvagn är backning nästan alltid nödvändigt när infarten är under 4 m bred.",
  },
  {
    q: "Är AR-måttet exakt?",
    a: "Nej – AR-vyn använder en typisk mobilkameras bildvinkel och det avstånd du anger, så räkna med cirka ±10 %. Den är till för att se proportionerna på plats. Använd planvyn och tumstocken för beslutet.",
  },
];

type Search = {
  /** fordons-id ur lagret */
  v?: string;
  /** uppfartens bredd */
  b?: number;
  /** tillgänglig längd */
  l?: number;
  /** fri höjd */
  h?: number;
  /** grindöppning */
  g?: number;
  /** fordonstyp för egna mått */
  t?: BodyType;
};

const num = (raw: unknown, min: number, max: number): number | undefined => {
  const n = typeof raw === "string" ? Number(raw.replace(",", ".")) : Number(raw);
  if (!Number.isFinite(n) || n < min || n > max) return undefined;
  return Math.round(n * 100) / 100;
};

export const Route = createFileRoute("/verktyg/passar-den-hos-mig")({
  validateSearch: (raw: Record<string, unknown>): Search => ({
    v: typeof raw.v === "string" && raw.v.length < 80 ? raw.v : undefined,
    b: num(raw.b, 2, 8),
    l: num(raw.l, 4, 16),
    h: num(raw.h, 0, 5),
    g: num(raw.g, 0, 6),
    t: typeof raw.t === "string" && raw.t in BODY_LABEL ? (raw.t as BodyType) : undefined,
  }),
  
  head: () => ({
    meta: [
      { title: "Passar husbilen hos mig? Mät uppfarten i AR | AK Husbil" },
      {
        name: "description",
        content:
          "Mät om husbilen eller husvagnen får plats på din uppfart: planvy i skala, fri höjd, grind, markis och snövallar – plus AR-läge i mobilen.",
      },
      {
        name: "keywords",
        content:
          "husbil mått, hur bred är en husbil, husbil höjd, parkera husbil hemma, husbil garage höjd, husvagn mått uppfart",
      },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Passar husbilen hos mig?" },
      {
        property: "og:description",
        content:
          "Planvy i skala + AR i mobilen: se om fordonet får plats innan du köper.",
      },
      { property: "og:url", content: URL_SELF },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL_SELF }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "FAQPage",
              mainEntity: FAQ.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
            {
              "@type": "WebApplication",
              name: "Passar den hos mig? – mätverktyg för husbil och husvagn",
              url: URL_SELF,
              applicationCategory: "UtilitiesApplication",
              operatingSystem: "Web",
              offers: { "@type": "Offer", price: "0", priceCurrency: "SEK" },
              provider: { "@type": "AutoDealer", name: "AK Husbil & Fritid" },
            },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

const ICON = {
  ok: <CheckCircle2 size={18} className="text-emerald-500" />,
  warn: <AlertTriangle size={18} className="text-amber-500" />,
  fail: <XCircle size={18} className="text-destructive" />,
};

function NumField({
  label,
  hint,
  value,
  onChange,
  min,
  max,
  step = 0.1,
}: {
  label: string;
  hint?: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium">{label}</span>
      {hint && <span className="mt-0.5 block text-xs text-muted-foreground">{hint}</span>}
      <div className="mt-2 flex items-center gap-3">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full accent-primary"
        />
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-20 rounded-lg border border-border bg-background px-2 py-1.5 text-sm"
        />
      </div>
    </label>
  );
}

function Page() {
  // Lagret hämtas i bakgrunden – verktyget ska aldrig blockeras av en loader.
  const fetchListings = useServerFn(getFyndanuListings);
  const listings = useQuery({
    queryKey: ["fyndanu-listings-fit"],
    queryFn: () => fetchListings(),
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const items = (listings.data?.items ?? []) as FyndanuListing[];
  const sp = Route.useSearch() as Search;
  const [selected, setSelected] = useState<string>(sp.v ?? "manual");
  const [bodyType, setBodyType] = useState<BodyType>(sp.t ?? "halvintegrerad");
  const [manual, setManual] = useState<{
    length: number;
    width: number;
    height: number;
    awning: number;
  }>(() => ({
    length: 7.4,
    ...BODY_DEFAULTS[(sp.t ?? "halvintegrerad") as BodyType],
  }));
  const [site, setSite] = useState<Site>(() => ({
    ...DEFAULT_SITE,
    ...(sp.b !== undefined ? { driveWidth: sp.b } : null),
    ...(sp.l !== undefined ? { driveLength: sp.l } : null),
    ...(sp.h !== undefined ? { clearHeight: sp.h } : null),
    ...(sp.g !== undefined ? { gateWidth: sp.g } : null),
  }));
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState<SavedSite[]>([]);
  const [siteName, setSiteName] = useState("");

  useEffect(() => {
    setSaved(loadSites());
  }, []);

  const dims: VehicleDims = useMemo(() => {
    if (selected !== "manual") {
      const it = items.find((i) => i.id === selected);
      if (it) return dimsFor(it.title, it.length ?? null, it.category);
    }
    return { ...manual, bodyType };
  }, [selected, items, manual, bodyType]);

  const checks = useMemo(() => evaluateFit(dims, site), [dims, site]);
  const total = verdict(checks);
  const selectedItem = items.find((i) => i.id === selected);

  return (
    <>
      <PageHeader
        label="Verktyg · Mått & plats"
        title={
          <>
            Passar den <span className="font-italic-display text-accent">hos mig</span>?
          </>
        }
        sub="Mät uppfarten en gång – vi visar om fordonet får plats med speglar, dörrsvep, markis och snövallar. Plus AR-läge så du ser den på plats i mobilen."
      />

      <section className="container-edge py-10">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,380px)_1fr]">
          {/* Indata */}
          <div className="space-y-6">
            <div className="rounded-3xl border border-border bg-card p-6">
              <p className="eyebrow">1. Välj fordon</p>
              <select
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                className="mt-3 w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm"
              >
                <option value="manual">Egna mått</option>
                {items.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.title}
                  </option>
                ))}
              </select>

              {selected === "manual" ? (
                <div className="mt-5 space-y-5">
                  <div>
                    <span className="text-sm font-medium">Fordonstyp</span>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(Object.keys(BODY_DEFAULTS) as BodyType[]).map((b) => (
                        <button
                          key={b}
                          onClick={() => {
                            setBodyType(b);
                            setManual((m) => ({ ...m, ...BODY_DEFAULTS[b] }));
                          }}
                          className={`rounded-full border px-3 py-1.5 text-xs ${
                            bodyType === b
                              ? "border-accent bg-accent text-accent-foreground"
                              : "border-border bg-background hover:bg-secondary"
                          }`}
                        >
                          {BODY_LABEL[b]}
                        </button>
                      ))}
                    </div>
                  </div>
                  <NumField
                    label="Längd (m)"
                    value={manual.length}
                    min={4}
                    max={10}
                    onChange={(v) => setManual((m) => ({ ...m, length: v }))}
                  />
                  <NumField
                    label="Karossbredd (m)"
                    hint="Utan speglar – vi lägger på 60 cm automatiskt"
                    value={manual.width}
                    min={1.8}
                    max={2.6}
                    step={0.01}
                    onChange={(v) => setManual((m) => ({ ...m, width: v }))}
                  />
                  <NumField
                    label="Höjd (m)"
                    hint="Inkl. takluckor, AC och solpaneler"
                    value={manual.height}
                    min={2}
                    max={3.6}
                    step={0.01}
                    onChange={(v) => setManual((m) => ({ ...m, height: v }))}
                  />
                </div>
              ) : (
                <div className="mt-4 rounded-2xl bg-secondary/40 p-4 text-sm">
                  <p className="font-medium">{selectedItem?.title}</p>
                  <p className="mt-1 text-muted-foreground">
                    {BODY_LABEL[dims.bodyType]} · {dims.length.toFixed(2).replace(".", ",")} m lång ·{" "}
                    {mirrorWidth(dims).toFixed(2).replace(".", ",")} m med speglar ·{" "}
                    {dims.height.toFixed(2).replace(".", ",")} m hög
                  </p>
                  {selectedItem && (
                    <Link
                      to="/fordon"
                      className="mt-3 inline-flex items-center gap-1 text-xs text-accent hover:underline"
                    >
                      Se fordonet i lagret <ArrowRight size={12} />
                    </Link>
                  )}
                </div>
              )}
            </div>

            <div className="rounded-3xl border border-border bg-card p-6">
              <p className="eyebrow">2. Mät platsen hemma</p>
              <div className="mt-4 space-y-5">
                <NumField
                  label="Uppfartens bredd (m)"
                  value={site.driveWidth}
                  min={2}
                  max={8}
                  onChange={(v) => setSite((s) => ({ ...s, driveWidth: v }))}
                />
                <NumField
                  label="Tillgänglig längd (m)"
                  value={site.driveLength}
                  min={4}
                  max={16}
                  onChange={(v) => setSite((s) => ({ ...s, driveLength: v }))}
                />
                <NumField
                  label="Fri höjd (m)"
                  hint="Portöppning, tak eller grenar. 0 = fri himmel."
                  value={site.clearHeight}
                  min={0}
                  max={5}
                  onChange={(v) => setSite((s) => ({ ...s, clearHeight: v }))}
                />
                <NumField
                  label="Grindöppning (m)"
                  hint="0 = ingen grind"
                  value={site.gateWidth}
                  min={0}
                  max={6}
                  onChange={(v) => setSite((s) => ({ ...s, gateWidth: v }))}
                />
              </div>

              <div className="mt-6 border-t border-border pt-5">
                <p className="text-sm font-medium">Spara platsen</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Måtten sparas bara i din egen telefon eller dator – inget skickas till oss.
                </p>
                <div className="mt-3 flex gap-2">
                  <input
                    value={siteName}
                    onChange={(e) => setSiteName(e.target.value)}
                    placeholder="T.ex. Uppfarten hemma"
                    className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
                  />
                  <button
                    onClick={() => {
                      setSaved(saveSite(siteName, site));
                      setSiteName("");
                    }}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-accent bg-accent px-3 py-2 text-sm text-accent-foreground"
                  >
                    <Bookmark size={14} /> Spara
                  </button>
                </div>
                {saved.length > 0 && (
                  <ul className="mt-3 space-y-2">
                    {saved.map((s) => (
                      <li
                        key={s.id}
                        className="flex items-center gap-2 rounded-xl bg-secondary/40 px-3 py-2 text-sm"
                      >
                        <button
                          onClick={() => setSite(s.site)}
                          className="flex-1 text-left hover:text-accent"
                        >
                          <span className="font-medium">{s.name}</span>
                          <span className="ml-2 text-xs text-muted-foreground">
                            {s.site.driveWidth.toFixed(1).replace(".", ",")} ×{" "}
                            {s.site.driveLength.toFixed(1).replace(".", ",")} m
                          </span>
                        </button>
                        <button
                          onClick={() => setSaved(removeSite(s.id))}
                          aria-label={`Ta bort ${s.name}`}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* Resultat */}
          <div className="space-y-6">
            <div
              className={`rounded-3xl border p-6 ${
                total.level === "ok"
                  ? "border-emerald-500/40 bg-emerald-500/5"
                  : total.level === "warn"
                    ? "border-amber-500/40 bg-amber-500/5"
                    : "border-destructive/40 bg-destructive/5"
              }`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  {ICON[total.level]}
                  <h2 className="font-display text-2xl">{total.text}</h2>
                </div>
                <button
                  onClick={() => {
                    const q = new URLSearchParams({
                      b: String(site.driveWidth),
                      l: String(site.driveLength),
                      h: String(site.clearHeight),
                      g: String(site.gateWidth),
                    });
                    if (selected !== "manual") q.set("v", selected);
                    else q.set("t", bodyType);
                    const url = `${URL_SELF}?${q.toString()}`;
                    void navigator.clipboard?.writeText(url).then(
                      () => {
                        setCopied(true);
                        window.setTimeout(() => setCopied(false), 2500);
                      },
                      () => setCopied(false),
                    );
                  }}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-secondary"
                >
                  <LinkIcon size={13} /> {copied ? "Länk kopierad!" : "Kopiera länk till mina mått"}
                </button>
              </div>
              <ul className="mt-5 space-y-3">
                {checks.map((c) => (
                  <li key={c.id} className="flex gap-3 rounded-2xl bg-background/70 p-3.5">
                    <span className="mt-0.5 shrink-0">{ICON[c.level]}</span>
                    <span>
                      <span className="block text-sm font-medium">{c.label}</span>
                      <span className="mt-0.5 block text-sm text-muted-foreground">{c.detail}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="eyebrow flex items-center gap-2">
                <Ruler size={13} /> Planvy i skala
              </p>
              <div className="mt-3">
                <FitPlan dims={dims} site={site} />
              </div>
            </div>

            <DeferredIsland
              load={loadTurnSim}
              componentProps={{ dims, site }}
              minHeight={420}
              label="Laddar svängsimulering…"
            />

            <DeferredIsland
              load={loadArSilhouette}
              componentProps={{
                dims,
                label: `${BODY_LABEL[dims.bodyType].toLowerCase()} på ${dims.length.toFixed(2).replace(".", ",")} m`,
              }}
              minHeight={360}
              label="Laddar AR-vy…"
            />
          </div>
        </div>
      </section>

      <section className="container-edge pb-16">
        <div className="prose-editorial max-w-3xl">
          <h2>Så mäter du rätt – tre mått som avgör allt</h2>
          <p>
            Nio av tio gånger när en kund ringer och undrar om husbilen får plats hemma handlar det
            inte om längden. Det handlar om bredden med utfällda speglar, den fria höjden vid
            portöppningen och hur mycket plats som blir kvar när snövallen växer i februari. Mät med
            tumstock, inte med ögonmått: en uppfart som ser ut att vara fyra meter bred är i regel
            3,4 m mellan häcken och husgrunden.
          </p>
          <h3>1. Bredd – räkna alltid med speglarna</h3>
          <p>
            Karossen på en halvintegrerad ligger kring 2,30 m. Med utfällda backspeglar blir det
            ungefär 2,90 m. Ska du kunna gå längs långsidan behöver du 60 cm till, och entrédörren
            svänger ut ytterligare cirka 75 cm. Det är därför verktyget flaggar redan vid 3,2 m
            uppfartsbredd även om fordonet i teorin "får plats".
          </p>
          <h3>2. Höjd – broschyrmåttet är inte ditt mått</h3>
          <p>
            Takmonterad luftkonditionering, satellit eller solpaneler lägger på 10–25 cm på
            broschyrhöjden. Har du en portöppning på 3,00 m och en alkov på 3,10 m går det inte,
            hur mycket du än släpper ut luft ur däcken. Mät portens lägsta punkt – ofta sitter en
            balk eller portmotor 5–10 cm under själva öppningen.
          </p>
          <h3>3. Längd – tänk på svängen in, inte bara platsen</h3>
          <p>
            Ett 7,4 m långt ekipage behöver mer än 7,4 m. Du behöver 50 cm bakom för att öppna
            bakluckan eller garageluckan, och du behöver kunna svänga in: med en uppfart som
            ansluter i rät vinkel mot gatan krävs i praktiken 6–7 m fri gatubredd för att komma in
            i ett svep.
          </p>
          <h3>Svängen in – det som oftast fäller köpet</h3>
          <p>
            Vi har mätt in fler uppfarter än vi kan räkna och det är nästan alltid svängen, inte
            själva platsen, som blir problemet. En halvintegrerad på 7,4 m har cirka 13 m
            vändcirkel och ett frontöverhäng på drygt en meter som svänger ut över motsatt kant när
            du vrider fullt. Därför behöver du 6,5–7,5 m fri gata för att komma in i ett svep i en
            3,5 m bred infart – och står grannens bil parkerad mittemot försvinner marginalen.
            Simuleringen ovan ritar hela svepytan så du ser exakt var det tar i grindstolpen.
          </p>
          <p>
            Två saker löser det i praktiken: bredda infartsöppningen till 4,5–5 m (oftast en fråga
            om en meter häck) eller backa in i två drag. Med husvagn är backning normalfallet –
            vagnen svänger in först och du utnyttjar hela gatan.
          </p>
          <h3>Vinterverklighet i Norrbotten</h3>
          <p>
            Vi levererar fordon i Kalix, Luleå och uppåt Tornedalen och ser samma sak varje år:
            marginalen som fanns i september är borta i mars. Snövallar tar 20–40 cm per sida och
            plogbilen bygger på ytterligare. Har du under 40 cm marginal i bredd bör du planera för
            vinterförvaring – vi hjälper gärna till att hitta en plats.
          </p>
          <h3>Vad gör du om det inte passar?</h3>
          <ul>
            <li>
              Titta på en kortare modell – skillnaden mellan 7,4 m och 6,4 m är oftast en
              sittgrupp, inte en sovplats.
            </li>
            <li>
              En campervan är 2,05 m bred och 2,55 m hög och passar i de flesta uppfarter och många
              carportar.
            </li>
            <li>
              Vinterförvaring plus sommarparkering på uppfarten är den vanligaste lösningen hos
              våra kunder.
            </li>
          </ul>
          <p>
            Osäker? Ring{" "}
            <a href="tel:+46708629743">070-862 97 43</a> med dina mått – vi har mätt in fler
            uppfarter än vi kan räkna och säger rakt ut om det blir bra eller inte.
          </p>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-3">
          <Link
            to="/verktyg/korkort-husbil"
            className="rounded-2xl border border-border bg-card p-5 hover:border-accent"
          >
            <p className="font-medium">Körkortskoll</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Vilket körkort krävs för totalvikten?
            </p>
          </Link>
          <Link
            to="/verktyg/manadskostnad-husbil"
            className="rounded-2xl border border-border bg-card p-5 hover:border-accent"
          >
            <p className="font-medium">Månadskostnad</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Vad landar husbilen på per månad?
            </p>
          </Link>
          <Link to="/fordon" className="rounded-2xl border border-border bg-card p-5 hover:border-accent">
            <p className="font-medium">Vårt lager</p>
            <p className="mt-1 text-sm text-muted-foreground">Husbilar och husvagnar i Kalix.</p>
          </Link>
        </div>

        <div className="prose-editorial mt-12 max-w-3xl">
          <h2>Vanliga frågor om mått och plats</h2>
          {FAQ.map((f) => (
            <div key={f.q}>
              <h3>{f.q}</h3>
              <p>{f.a}</p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
